import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  PlayCircle,
  Loader2,
  Repeat,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRole } from '@/hooks/useRole';
import { usePlaybooks, usePlaybookInstances, useRunGenerateInstances } from './usePlaybooks';
import {
  FREQUENCY_LABELS,
  INSTANCE_STATUS_CLASSES,
  INSTANCE_STATUS_LABELS,
  type PlaybookInstanceStatus,
} from './types';

export function PlaybooksPage() {
  const { isManager } = useRole();
  const [statusFilter, setStatusFilter] = useState<'all' | PlaybookInstanceStatus>('all');
  const { data: playbooks = [], isLoading: loadingPb } = usePlaybooks();
  const { data: instances = [], isLoading: loadingInstances } = usePlaybookInstances(
    statusFilter === 'all' ? {} : { status: statusFilter },
  );
  const runGenerate = useRunGenerateInstances();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const summary = useMemo(() => {
    return {
      total: instances.length,
      pending: instances.filter((i) => i.status === 'pending').length,
      inProgress: instances.filter((i) => i.status === 'in_progress').length,
      completed: instances.filter((i) => i.status === 'completed').length,
    };
  }, [instances]);

  if (!isManager) {
    return (
      <div className="rounded-xl border border-border bg-white p-8 text-center text-muted-foreground">
        <p className="text-sm">Acesso restrito a admin/gerente.</p>
      </div>
    );
  }

  const togglePb = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Playbooks</h1>
          <p className="text-sm text-muted-foreground">
            Rotinas operacionais que viram tarefas no Meu Dia. Geração automática diária às 06:00.
          </p>
        </div>
        <Button
          onClick={() => runGenerate.mutate(undefined)}
          disabled={runGenerate.isPending}
          variant="outline"
        >
          {runGenerate.isPending ? (
            <Loader2 size={14} className="mr-1.5 animate-spin" />
          ) : (
            <PlayCircle size={14} className="mr-1.5" />
          )}
          Gerar agora (hoje)
        </Button>
      </div>

      {/* Templates de playbook */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-muted-foreground">Templates</h2>
        {loadingPb ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg border bg-muted" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {playbooks.map((pb) => {
              const isOpen = expanded.has(pb.id);
              return (
                <Card key={pb.id}>
                  <button
                    type="button"
                    onClick={() => togglePb(pb.id)}
                    className="flex w-full items-center gap-3 p-4 text-left hover:bg-secondary/30"
                  >
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <Repeat size={16} className="text-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground">{pb.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {pb.description ?? '—'}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {FREQUENCY_LABELS[pb.frequency]}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {pb.steps.length} passos
                    </Badge>
                  </button>
                  {isOpen && (
                    <CardContent className="border-t pl-12 pr-4 pt-4">
                      <ol className="space-y-2">
                        {pb.steps.map((step) => (
                          <li key={step.id} className="flex items-start gap-3 text-sm">
                            <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                              {step.order_index}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium">{step.title}</p>
                              {step.description && (
                                <p className="text-xs text-muted-foreground">{step.description}</p>
                              )}
                              <div className="mt-1 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                                <span>Prazo: D+{step.due_offset_days}</span>
                                <span>· Prioridade: {step.priority}</span>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Instâncias geradas */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-muted-foreground">Instâncias geradas</h2>
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="in_progress">Em andamento</SelectItem>
              <SelectItem value="completed">Concluídas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryCard label="Total" value={summary.total} icon={Repeat} />
          <SummaryCard label="Pendentes" value={summary.pending} icon={Clock} tone="muted" />
          <SummaryCard
            label="Em andamento"
            value={summary.inProgress}
            icon={AlertCircle}
            tone="amber"
          />
          <SummaryCard
            label="Concluídas"
            value={summary.completed}
            icon={CheckCircle2}
            tone="income"
          />
        </div>

        {loadingInstances ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg border bg-muted" />
            ))}
          </div>
        ) : instances.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <p className="text-sm">Nenhuma instância gerada ainda.</p>
              <p className="mt-1 text-xs">
                Use "Gerar agora" para criar para clientes ativos hoje.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="divide-y rounded-xl border border-border bg-white">
            {instances.map((i) => {
              const pct =
                i.task_total > 0 ? Math.round((i.task_completed / i.task_total) * 100) : 0;
              return (
                <div key={i.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{i.playbook_name}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {FREQUENCY_LABELS[i.playbook_frequency]}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${INSTANCE_STATUS_CLASSES[i.status]}`}
                      >
                        {INSTANCE_STATUS_LABELS[i.status]}
                      </Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span>{i.client_name ?? '—'}</span>
                      <span>·</span>
                      <span>
                        {format(new Date(i.period_start), 'dd/MM/yyyy', { locale: ptBR })}
                        {i.period_start !== i.period_end &&
                          ` – ${format(new Date(i.period_end), 'dd/MM/yyyy', { locale: ptBR })}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="w-24">
                      <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                        <div
                          className="h-full rounded-full bg-income transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <span className="tabular-nums">
                      {i.task_completed}/{i.task_total}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: number;
  icon: typeof Clock;
  tone?: 'muted' | 'amber' | 'income';
}

function SummaryCard({ label, value, icon: Icon, tone }: SummaryCardProps) {
  const toneClasses = {
    muted: 'text-muted-foreground',
    amber: 'text-amber-700',
    income: 'text-income',
  } as const;
  const cls = tone ? toneClasses[tone] : 'text-primary';
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${cls}`} />
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="text-lg font-bold text-foreground">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
